import React, { useCallback } from 'react';
import { useDragLayer } from 'react-dnd';
import { DashboardChartRecordDto } from 'libs/end-to-end/src/dashboard';
import { CHART_DRAG_TYPE } from 'apps/sigma2-ui/src/pages/Dashboards/DragDropContext';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

const layerStyles: React.CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
};

function getItemStyles(
  initialOffset: { x: number; y: number } | null,
  currentOffset: { x: number; y: number } | null,
) {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none',
    };
  }

  const { x, y } = currentOffset;

  // Card dimensions: w-80 = 320px width, estimated 160px height
  const cardWidth = 320;
  const cardHeight = 160;
  const scale = 0.8;

  // Calculate scaled dimensions
  const scaledWidth = cardWidth * scale;
  const scaledHeight = cardHeight * scale;

  // Position so the bottom right corner aligns with cursor
  const leftOffset = x - scaledWidth;
  const topOffset = y - scaledHeight;

  const transform = `translate(${leftOffset}px, ${topOffset}px) scale(${scale})`;
  return {
    transform,
    WebkitTransform: transform,
  };
}

interface DragPreviewProps {}

export const DragPreview: React.FC<DragPreviewProps> = () => {
  const { itemType, isDragging, item, initialOffset, currentOffset } =
    useDragLayer((monitor) => ({
      item: monitor.getItem() as { chart: DashboardChartRecordDto } | null,
      itemType: monitor.getItemType(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getClientOffset(),
      isDragging: monitor.isDragging(),
    }));

  const renderItem = useCallback(() => {
    if (!item?.chart) {
      return null;
    }

    return (
      <Card
        paddingClassName="p-4"
        className="opacity-70 shadow-lg border-2 border-blue-300 w-80"
      >
        <div className="space-y-2">
          <S2Typography
            variant="headline-4"
            translatedContent={[{ text: item.chart.name }]}
          />
          <S2Typography
            variant="body-6"
            translatedContent={[{ text: item.chart.description ?? '' }]}
          />
        </div>
      </Card>
    );
  }, [item]);

  if (!isDragging || itemType !== CHART_DRAG_TYPE) {
    return null;
  }

  return (
    <div style={layerStyles}>
      <div style={getItemStyles(initialOffset, currentOffset)}>
        {renderItem()}
      </div>
    </div>
  );
};
